import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, X, ArrowLeft, Save } from "lucide-react";

const FIELDS = [
  { key: "date", label: "Purchase date", ops: ["between", "before", "after"] },
  { key: "product_name", label: "Product name", ops: ["contains", "equals", "starts_with"] },
  { key: "city", label: "City", ops: ["is", "is_one_of", "contains"] },
  { key: "pin_code", label: "Pin code", ops: ["is", "starts_with", "is_one_of"] },
  { key: "order_status", label: "Order status", ops: ["is", "is_one_of", "contains"] },
  { key: "name", label: "Name", ops: ["contains", "is_present"] },
  { key: "email", label: "Email", ops: ["contains", "is_present"] },
  { key: "phone", label: "Phone", ops: ["contains", "is_present"] },
];
const OP_LABELS = { between: "between", before: "before", after: "after", contains: "contains", equals: "equals", is: "is", starts_with: "starts with", is_one_of: "is one of (comma-sep)", is_present: "is present" };

function newCondition(field = "product_name") {
  const f = FIELDS.find((x) => x.key === field);
  const op = f.ops[0];
  return { field, operator: op, value: op === "between" ? { from: "", to: "" } : "" };
}

export default function AudienceBuilder() {
  const { audienceId } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [conditions, setConditions] = useState([newCondition()]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (audienceId) {
      api.get(`/audiences/${audienceId}`).then((r) => {
        setName(r.data.name);
        setConditions(r.data.conditions?.length ? r.data.conditions : [newCondition()]);
      }).catch(() => toast.error("Audience not found"));
    }
  }, [audienceId]);

  const condKey = useMemo(() => JSON.stringify(conditions), [conditions]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/audiences/preview", { conditions }).then((r) => setPreview(r.data)).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [condKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (i, patch) => setConditions(conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const changeField = (i, field) => {
    const f = FIELDS.find((x) => x.key === field);
    update(i, { field, operator: f.ops[0], value: f.ops[0] === "between" ? { from: "", to: "" } : "" });
  };
  const changeOp = (i, op) => update(i, { operator: op, value: op === "between" ? { from: "", to: "" } : op === "is_present" ? "" : typeof conditions[i].value === "string" ? conditions[i].value : "" });

  const save = async () => {
    if (!name.trim()) return toast.error("Give your audience a name");
    setSaving(true);
    try {
      if (audienceId) await api.put(`/audiences/${audienceId}`, { name, conditions });
      else await api.post("/audiences", { name, conditions });
      toast.success("Audience saved");
      navigate("/audiences");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button data-testid="builder-back-button" onClick={() => navigate("/audiences")} className="text-slate-400 hover:text-slate-100 p-1"><ArrowLeft size={19} /></button>
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-0.5">Query composer · all conditions must match (AND)</p>
          <h1 className="font-heading text-2xl font-bold text-slate-50">{audienceId ? "Edit audience" : "New audience"}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6 items-start">
        <div className="space-y-4 min-w-0">
          <div className="bg-[#111827] border border-slate-800 rounded-xl p-5 space-y-4">
            <Input data-testid="audience-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder='Audience name, e.g. "Walker buyers – Mumbai"' className="bg-slate-900 border-slate-700 text-slate-100" />
            <div className="space-y-3">
              {conditions.map((c, i) => {
                const f = FIELDS.find((x) => x.key === c.field);
                return (
                  <div key={i} data-testid="condition-row" className="p-3 rounded-lg bg-slate-900/70 border border-slate-800 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-slate-500 w-8">{i === 0 ? "IF" : "AND"}</span>
                      <Select value={c.field} onValueChange={(v) => changeField(i, v)}>
                        <SelectTrigger data-testid="condition-field-select" className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs flex-1 min-w-0"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                          {FIELDS.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={c.operator} onValueChange={(v) => changeOp(i, v)}>
                        <SelectTrigger data-testid="condition-operator-select" className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs flex-1 min-w-0"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                          {f.ops.map((op) => <SelectItem key={op} value={op}>{OP_LABELS[op]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button data-testid="condition-remove-button" onClick={() => setConditions(conditions.filter((_, j) => j !== i))} className="text-slate-500 hover:text-rose-400"><X size={14} /></button>
                    </div>
                    {c.field === "date" && c.operator === "between" ? (
                      <div className="flex gap-2 pl-10">
                        <Input data-testid="condition-date-from" type="date" value={c.value?.from || ""} onChange={(e) => update(i, { value: { ...c.value, from: e.target.value } })} className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs min-w-0" />
                        <Input data-testid="condition-date-to" type="date" value={c.value?.to || ""} onChange={(e) => update(i, { value: { ...c.value, to: e.target.value } })} className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs min-w-0" />
                      </div>
                    ) : c.field === "date" ? (
                      <div className="pl-10">
                        <Input data-testid="condition-date-value" type="date" value={c.value || ""} onChange={(e) => update(i, { value: e.target.value })} className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs" />
                      </div>
                    ) : c.operator !== "is_present" ? (
                      <div className="pl-10">
                        <Input data-testid="condition-value-input" value={c.value || ""} onChange={(e) => update(i, { value: e.target.value })} placeholder={c.operator === "is_one_of" ? "Mumbai, Pune, Delhi" : "Value…"} className="bg-slate-900 border-slate-700 text-slate-200 h-8 text-xs" />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <Button data-testid="add-condition-button" variant="outline" onClick={() => setConditions([...conditions, newCondition()])} className="w-full border-dashed border-slate-700 text-slate-300 hover:bg-slate-800">
              <Plus size={14} className="mr-1.5" /> Add condition
            </Button>
          </div>
          <Button data-testid="save-audience-button" onClick={save} disabled={saving} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-semibold">
            <Save size={15} className="mr-2" /> {saving ? "Saving…" : "Save audience"}
          </Button>
        </div>

        <div className="space-y-4 min-w-0">
          <div className="bg-[#111827] border border-emerald-500/30 rounded-xl p-5 flex flex-wrap items-center gap-x-8 gap-y-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Matching customers</div>
              <div data-testid="audience-matching-count-badge" className="font-heading text-4xl font-bold text-emerald-400">{preview ? preview.total.toLocaleString() : "…"}</div>
            </div>
            <div className="text-xs text-slate-400 space-y-1 font-mono">
              <div data-testid="audience-email-reach">✉ {preview?.email_reach ?? "…"} reachable by email</div>
              <div data-testid="audience-wa-reach">✆ {preview?.wa_reach ?? "…"} reachable on WhatsApp</div>
            </div>
            <span className="ml-auto animate-pulse bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 px-3 py-1 rounded-full text-xs font-mono">live</span>
          </div>

          <div className="bg-[#111827] border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-800 text-xs font-mono uppercase tracking-widest text-slate-500">Preview (first 20 matches)</div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    {["Date", "Name", "Email", "Phone", "Product", "City", "Pin"].map((h) => <TableHead key={h} className="text-slate-400 text-xs whitespace-nowrap">{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!preview?.sample?.length ? (
                    <TableRow className="border-slate-800"><TableCell colSpan={7} className="text-center text-slate-500 py-8" data-testid="preview-empty">No matches yet</TableCell></TableRow>
                  ) : preview.sample.map((c) => (
                    <TableRow key={c.id} data-testid="preview-row" className="border-slate-800 hover:bg-slate-800/40">
                      <TableCell className="text-slate-300 text-xs font-mono whitespace-nowrap">{c.date || "—"}</TableCell>
                      <TableCell className="text-slate-200 text-xs whitespace-nowrap">{c.name || "—"}</TableCell>
                      <TableCell className="text-slate-300 text-xs whitespace-nowrap">{c.email || "—"}</TableCell>
                      <TableCell className="text-slate-300 text-xs font-mono whitespace-nowrap">{c.phone || "—"}</TableCell>
                      <TableCell className="text-slate-300 text-xs whitespace-nowrap max-w-[160px] truncate">{c.product_name || "—"}</TableCell>
                      <TableCell className="text-slate-300 text-xs whitespace-nowrap">{c.city || "—"}</TableCell>
                      <TableCell className="text-slate-300 text-xs font-mono">{c.pin_code || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
